import { AnimatePresence } from "framer-motion";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Layout from "../components/Layout";
import Navbar from "../components/Layout/Navbar";
import Sidebar from "../components/Layout/Sidebar";
import { otherPages, profilePages, routes, sideBarPages, supplierAdminDashboardKey } from "config/paths";
import { AppConfigProvider } from "../context/AppConfig";
import { AuthProvider } from "../context";
import { AppSpinner } from "../components/AppSpinner";
import { useAuth0 } from "@auth0/auth0-react";
import { isAllowedToView, isModuleAvailable } from "utils/AuthUtils";
import { useIsFetching } from "react-query";
import { useEffect } from "react";
import { ChakraProvider } from "@chakra-ui/react";
import EsgTheme from "../theme";
import { NavbarProvider } from "../components/Layout/Navbar/NavbarProvider";
import { RightSidebarProvider } from "../context/RightSidebarContext";
import { noSidebarRoutes } from "../config/sidebar-config";
import * as Sentry from "@sentry/react";
import { isDevEnvironment } from "../pages/ISSB/demoUtils";
import { ConfirmationDialogProvider } from "../context/Mutation/ConfirmationDialogContext";
import { RerenderProvider } from "rerenderer";

// Sentry error tracking
// ..but not on local or staging
if (!isDevEnvironment()) {
  Sentry.init({
    dsn: "https://ccedb585ec475529c696e79acd18a23c@o4509004123471872.ingest.de.sentry.io/4509004141559888",
  });
}

function ESGApp() {
  const { user } = useAuth0();
  const location = useLocation();

  const initialRoutes = [
    routes.issbRequirements,
    routes.dashboard,
    routes[supplierAdminDashboardKey],
    routes.supplierDashboard,
    routes.supplierQuestionnaire,
    routes.dataCollectionOrganisationQuestionnaires,
    routes.csrdQuestionnaires,
  ];
  const userRoles = user[process.env.REACT_APP_USER_PERMISSIONS_NAMESPACE] || [];

  useEffect(() => {
    if (user) {
      // set clarity user context
      window.clarity("identify", user?.email);
      window.clarity("set", "tenant_id", user?.org_id);
      console.log("Context set");
    }
  }, [user]);

  let mainRoute = routes.dashboard;

  // Finds the first from 'initialRoutes' to which the user has access
  const permittedRoute = initialRoutes.find((route) => {
    const routeInfo = sideBarPages.find((page) => {
      if (page.pathProps) {
        return page.pathProps.path === route;
      } else if (page.subMenus?.length > 0) {
        // Case with item which has sub-menus - in this case we need to find a match with some of the sub menus' routes,
        // instead of a match with parent item's route
        return page.subMenus.find((subMenu) => subMenu.pathProps?.path === route);
      }
      return null;
    });

    if (routeInfo?.linkProps?.requiredPermissions) {
      return (
        isAllowedToView(userRoles, routeInfo?.linkProps?.requiredPermissions) && isModuleAvailable(routeInfo?.module)
      );
    }
    return false;
  });

  console.log("Initial Route", permittedRoute);

  if (permittedRoute) {
    mainRoute = permittedRoute;
  }

  // HACK no sidebar for onboarding pages
  const isSidebarDisabled = noSidebarRoutes.includes(location.pathname);

  return (
    <AuthProvider>
      <AppConfigProvider>
        <AppSpinner />
        <RerenderProvider>
          <ConfirmationDialogProvider>
            <NavbarProvider>
              <RightSidebarProvider>
                <Layout
                  sidebar={
                    !isSidebarDisabled && (
                      <ChakraProvider theme={EsgTheme}>
                        <Sidebar />
                      </ChakraProvider>
                    )
                  }
                  navbar={!isSidebarDisabled && <Navbar />}
                >
                  <AnimatePresence presenceAffectsLayout exitBeforeEnter>
                    <Routes location={location} key={location.pathname}>
                      <Route index element={<Navigate to={mainRoute} />} />
                      {sideBarPages.map(({ pathProps, subMenus }) => {
                        if (subMenus && subMenus.length > 0) {
                          const routes = [];
                          if (pathProps) {
                            routes.push(<Route key={pathProps} {...pathProps} />);
                          }
                          return routes.concat(
                            subMenus.map((subMenu) => {
                              return <Route key={subMenu.pathProps} {...subMenu.pathProps} />;
                            })
                          );
                        }
                        return <Route key={pathProps} {...pathProps} />;
                      })}
                      {profilePages.map(({ pathProps }) => (
                        <Route key={pathProps} {...pathProps} />
                      ))}
                      {otherPages.map(({ pathProps }) => (
                        <Route key={pathProps} {...pathProps} />
                      ))}
                    </Routes>
                  </AnimatePresence>
                </Layout>
              </RightSidebarProvider>
            </NavbarProvider>
          </ConfirmationDialogProvider>
        </RerenderProvider>
      </AppConfigProvider>
    </AuthProvider>
  );
}

export default ESGApp;
